// components/lib/Global/InActivityTracker.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import { Constants } from "config/constants";

const InActivityTracker = ({ onLogout }) => {
  const { isAuthenticated, logout } = useAuth0();
  const history = useHistory();
  const [inactivityLimitReached, setInactivityLimitReached] = useState(false);
  const [counter, setCounter] = useState(10);
  const inactivityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // Events that reset inactivity timer
  const events = [
    "mousedown",
    "mousemove",
    "keydown",
    "keypress",
    "touchstart",
    "touchmove",
    "scroll",
    "wheel",
  ];

  // Cleanup timers
  const cleanupTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    const storedExpireAt = localStorage.getItem("expireAt");
    const currentExpireAt = storedExpireAt ? JSON.parse(storedExpireAt) : 0;
    const now = Date.now();

    // If the expiration time in storage is in the future (updated by another tab),
    // don't show the popup, but reset the timer for the remaining time.
    if (currentExpireAt > now) {
      const remaining =
        Math.min(currentExpireAt - now, Constants.INACTIVITY_TIMEOUT_MS) + 1000;
      inactivityTimerRef.current = setTimeout(checkInactivity, remaining);
    } else {
      setInactivityLimitReached(true);
      localStorage.setItem("InactivityLimitReached", "true");
      setCounter(10);
    }
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return;

    cleanupTimers();

    const expireAt = Date.now() + Constants.INACTIVITY_TIMEOUT_MS;
    localStorage.setItem("expireAt", JSON.stringify(expireAt));

    // Set new inactivity timer
    inactivityTimerRef.current = setTimeout(
      checkInactivity,
      Constants.INACTIVITY_TIMEOUT_MS
    );
  }, [isAuthenticated, cleanupTimers, checkInactivity]);

  // Check for existing inactivity state
  const checkInactivityState = useCallback(() => {
    if (isAuthenticated) {
      const limitReached =
        localStorage.getItem("InactivityLimitReached") === "true";
      if (limitReached) {
        setInactivityLimitReached(true);
        setCounter(10);
      }
    }
  }, [isAuthenticated]);

  // Handle logout
  const handleInactivityLogout = useCallback(async () => {
    try {
      cleanupTimers();

      // Clear app data
      Cookies.remove("role");
      localStorage.removeItem("InactivityLimitReached");
      localStorage.removeItem("expireAt");

      // Call parent logout or default
      if (onLogout) {
        onLogout();
      } else {
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
    } catch (error) {
      console.error("Inactivity logout error:", error);
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [logout, onLogout, cleanupTimers]);

  // Initialize on mount
  useEffect(() => {
    if (isAuthenticated) {
      resetInactivityTimer();
      checkInactivityState();
    }

    return cleanupTimers;
  }, [
    isAuthenticated,
    resetInactivityTimer,
    checkInactivityState,
    cleanupTimers,
  ]);

  // Setup event listeners
  useEffect(() => {
    if (isAuthenticated) {
      const handleUserActivity = () => {
        if (inactivityLimitReached) {
          return;
        }
        resetInactivityTimer();
      };

      events.forEach((event) => {
        window.addEventListener(event, handleUserActivity, { passive: true });
      });
      const unlisten = history.listen(handleUserActivity);
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          handleUserActivity();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, handleUserActivity);
        });
        unlisten();
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    inactivityLimitReached,
    history,
    resetInactivityTimer,
    cleanupTimers,
  ]);

  // Handle countdown
  useEffect(() => {
    if (inactivityLimitReached && isAuthenticated) {
      if (counter > 0) {
        countdownTimerRef.current = setTimeout(() => {
          setCounter((prev) => prev - 1);
        }, 1000);
      } else {
        handleInactivityLogout();
      }
    }

    return () => {
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
      }
    };
  }, [
    inactivityLimitReached,
    counter,
    isAuthenticated,
    handleInactivityLogout,
  ]);

  // Listen for storage events (other tabs)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "InactivityLimitReached") {
        if (event.newValue === "true") {
          if (isAuthenticated && !inactivityLimitReached) {
            setInactivityLimitReached(true);
            setCounter(10);
          }
        } else {
          // Activity detected in another tab (key removed or set to false)
          if (isAuthenticated && inactivityLimitReached) {
            setInactivityLimitReached(false);
            setCounter(10);
            cleanupTimers();
            resetInactivityTimer();
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [
    isAuthenticated,
    inactivityLimitReached,
    cleanupTimers,
    resetInactivityTimer,
  ]);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {inactivityLimitReached && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-filter backdrop-blur-sm z-50">
          <div className="overlay absolute inset-0 bg-gray-800 bg-opacity-50"></div>
          <div className="bg-white p-8 rounded-lg shadow-md relative z-10 max-w-md w-full mx-4">
            <h2 className="text-2xl font-semibold mb-4 text-center text-gray-800">
              Session Timeout
            </h2>
            <div className="flex flex-col items-center space-y-4">
              <p className="text-gray-700 text-center">
                You've been inactive for a while. For security reasons, you will
                be logged out soon.
              </p>
              <p className="text-gray-700 text-center">
                Logging out in{" "}
                <span className="font-bold text-red-600">{counter}</span>{" "}
                seconds...
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-red-500 h-2.5 rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${(counter / 10) * 100}%` }}
                ></div>
              </div>
              <div className="flex gap-3 w-full mt-4">
                <button
                  onClick={() => {
                    setInactivityLimitReached(false);
                    localStorage.removeItem("InactivityLimitReached");
                    setCounter(10);
                    cleanupTimers();
                    resetInactivityTimer();
                  }}
                  className="flex-1 bg-sflPrimary hover:bg-green-800 text-white font-medium py-3 px-4 rounded-md transition duration-200"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={handleInactivityLogout}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-3 px-4 rounded-md transition duration-200"
                >
                  Log Out Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InActivityTracker;
